import { useState, type ReactNode } from 'react';
import { AccountResponse, AccountType } from '@shared/api';
import { useCurrencySymbol } from '@shared/hooks';
import { cn, FormatUtils, PaletteUtils } from '@shared/utils';
import { Checkbox, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface AccountItemProps {
  className?: string;
  account: AccountResponse;
  onlyPaymentAccounts?: boolean;
  hideCheckbox?: boolean;
  value?: boolean;
  onChange?: (value: boolean) => void;
  children?: ReactNode;
}

export const AccountSelectItem = (props: AccountItemProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const symbol = useCurrencySymbol(props.account.currency);

  return (
    <div
      className={cn(
        'flex flex-col rounded-md dark:bg-light/5',
        PaletteUtils.isInsigniaPalette && 'bg-ice',
        PaletteUtils.isVcfcPalette && 'bg-ghost-white',
        props.className,
      )}
    >
      <div
        className={cn('flex items-center justify-between px-3 py-2', props.children && 'cursor-pointer')}
        onClick={props.children ? () => setOpen((prev) => !prev) : undefined}
      >
        <div className='flex items-center gap-4'>
          {!props.hideCheckbox && (!props.onlyPaymentAccounts || props.account.type === AccountType.Payment) && (
            <Checkbox value={props.value} onChange={props.onChange} />
          )}
          <div className='flex items-center gap-2'>
            {props.account.type === AccountType.Payment && (
              <div
                className={cn(
                  'inline-flex shrink-0 items-center gap-1 rounded border border-primary px-1.5 py-px',
                  PaletteUtils.isVcfcPalette && 'dark:border-light/50',
                )}
              >
                {symbol && (
                  <Text weight='semibold' size='xs' color='primary'>
                    {symbol}
                  </Text>
                )}
                <Text uppercase weight='semibold' size='xs' color='primary'>
                  {props.account.currency}
                </Text>
              </div>
            )}
            <div className='flex flex-col'>
              <Text size='sm'>
                {props.account.type === AccountType.Payment ? props.account.accountNumber : FormatUtils.toSliceNumber(props.account.accountNumber)}
              </Text>
              <Text className='min-w-0 flex-1 overflow-hidden text-ellipsis' size='xs' color='secondary'>
                {props.account.productName}
              </Text>
            </div>
          </div>
        </div>
        {props.children && (
          <Icon
            className={cn('shrink-0 -rotate-90 transition-transform duration-200', open && 'rotate-90')}
            color={PaletteUtils.getColor('primary', PaletteUtils.isVcfcPalette ? 'dark' : 'white')}
            icon={<ChevronIcon />}
          />
        )}
      </div>
      {open && props.children && (
        <div
          className={cn(
            'border-t px-3 py-2 pl-10',
            PaletteUtils.isInsigniaPalette && 'border-gray-silk dark:border-olive-gray',
            PaletteUtils.isVcfcPalette && 'border-light/30 dark:border-light/10',
          )}
        >
          {props.children}
        </div>
      )}
    </div>
  );
};
